// src/store/slices/organizationSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Organization, getUserOrganization, createOrganization, updateOrganization } from '../../utils/supabaseClient';

export interface OrganizationState {
	organization: Organization | null;
	isLoading: boolean;
	error: string | null;
	showSetupModal: boolean;
}

const initialState: OrganizationState = {
	organization: null,
	isLoading: false,
	error: null,
	showSetupModal: false,
};

// Async thunks
export const fetchUserOrganization = createAsyncThunk(
	'organization/fetchUserOrganization',
	async (userId: string) => {
		const organization = await getUserOrganization(userId);
		return organization;
	}
);

export const createUserOrganization = createAsyncThunk(
	'organization/createUserOrganization',
	async (name: string) => {
		const { data, error } = await createOrganization(name);
		if (error) throw error;
		return data;
	}
);

export const updateUserOrganization = createAsyncThunk(
	'organization/updateUserOrganization',
	async ({ organizationId, data }: { organizationId: string; data: Partial<Organization> }) => {
		const { data: updatedOrg, error } = await updateOrganization(organizationId, data);
		if (error) throw error;
		return updatedOrg;
	}
);

const organizationSlice = createSlice({
	name: 'organization',
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearOrganization: (state) => {
			state.organization = null;
		},
		setShowSetupModal: (state, action: PayloadAction<boolean>) => {
			state.showSetupModal = action.payload;
		},
		setOrganization: (state, action: PayloadAction<Organization | null>) => {
			state.organization = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch user organization
			.addCase(fetchUserOrganization.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchUserOrganization.fulfilled, (state, action) => {
				state.isLoading = false;
				state.organization = action.payload;
				state.showSetupModal = action.payload ? !action.payload.name : false;
				state.error = null;
			})
			.addCase(fetchUserOrganization.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to fetch organization';
			})
			// Create organization
			.addCase(createUserOrganization.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(createUserOrganization.fulfilled, (state, action) => {
				state.isLoading = false;
				state.organization = action.payload;
				state.showSetupModal = false;
				state.error = null;
			})
			.addCase(createUserOrganization.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to create organization';
			})
			// Update organization
			.addCase(updateUserOrganization.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(updateUserOrganization.fulfilled, (state, action) => {
				state.isLoading = false;
				state.organization = action.payload;
				state.showSetupModal = false;
				state.error = null;
			})
			.addCase(updateUserOrganization.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.error.message || 'Failed to update organization';
			});
	},
});

export const { clearError, clearOrganization, setShowSetupModal, setOrganization } = organizationSlice.actions;
export default organizationSlice.reducer;